import React, { useState } from 'react';
import { Phone, Mail, Clock, MessageSquare, Instagram, Send, CheckCircle2, ExternalLink, Sparkles } from 'lucide-react';
import api from '../api/axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/messages', formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      }, 3000);
    } catch (err) {
      alert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-8 h-8 text-gold-500" />,
      title: 'Phone',
      details: ['+92 333 5228094'],
      action: 'tel:+923335228094',
      buttonText: 'Call Now'
    },
    {
      icon: <MessageSquare className="w-8 h-8 text-green-500" />,
      title: 'WhatsApp',
      details: ['+92 333 5228094', 'Quick response guaranteed'],
      action: 'https://wa.me/923335228094',
      buttonText: 'Chat Now'
    },
    {
      icon: <Mail className="w-8 h-8 text-gold-500" />,
      title: 'Email',
      details: ['magicworld862@gmail.com'],
      action: 'mailto:magicworld862@gmail.com',
      buttonText: 'Send Email'
    },
    {
      icon: <Clock className="w-8 h-8 text-blue-500" />,
      title: 'Working Hours',
      details: ['Mon - Sun: 11:00 AM - 11:00 PM', 'Pre-orders: 24/7'],
      action: null,
      buttonText: 'View Schedule'
    }
  ];

  return (
    <div className="min-h-screen bg-background pb-20 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-green-900/10 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gold-500/10 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="container mx-auto px-6 pt-40 pb-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center p-3 bg-gold-500/10 rounded-full mb-6 relative group">
            <div className="absolute inset-0 bg-gold-500/20 rounded-full blur-xl group-hover:bg-gold-500/40 transition-colors" />
            <Phone className="w-8 h-8 text-gold-500 relative z-10 animate-shake" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-300 via-gold-500 to-gold-300 mb-6 font-serif">
            Contact Us
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            We'd love to hear from you! Reach out for orders, inquiries, or feedback.
          </p>
        </motion.div>

        {/* Contact Info Cards */}
        <section className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <Card className="bg-gray-900/40 border-gray-800 hover:border-gold-500/50 transition-all duration-300 h-full hover:shadow-lg hover:shadow-gold-500/10">
                  <CardContent className="p-8 text-center flex flex-col h-full items-center">
                    <div className="w-16 h-16 rounded-2xl bg-gray-800/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-inner border border-white/5">
                      {info.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 font-serif">{info.title}</h3>
                    <div className="space-y-1 mb-6 flex-grow">
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-gray-400 text-sm">{detail}</p>
                      ))}
                    </div>
                    {info.action && (
                      <Button
                        variant="outline"
                        className="w-full border-gold-500/30 text-gold-400 hover:bg-gold-500 hover:text-black group-hover:border-gold-500"
                        onClick={() => window.open(info.action, '_blank')}
                      >
                        {info.title === 'Phone' ? <Phone className="w-4 h-4 mr-2" /> :
                          info.title === 'WhatsApp' ? <MessageSquare className="w-4 h-4 mr-2" /> :
                            <Mail className="w-4 h-4 mr-2" />}
                        {info.buttonText}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 items-start">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-sm overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold-500 via-transparent to-gold-500 opacity-20" />
              <CardContent className="p-8 md:p-10">
                <h2 className="text-3xl font-bold text-white mb-2 font-serif flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-gold-500" /> Send Us a Message
                </h2>
                <p className="text-gray-400 mb-8">Fill out the form below and we'll get back to you shortly.</p>

                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 text-center"
                  >
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="text-green-500 w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                    <p className="text-gray-300">Thank you for contacting us. We will get back to you as soon as possible.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-100">Your Name *</label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="bg-gray-900/50 border-gray-700 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-all font-light"
                          placeholder="Enter your name"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-100">Phone *</label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="bg-gray-900/50 border-gray-700 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-all font-light"
                          placeholder="03xx xxxxxxx"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gold-100">Email *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-gray-900/50 border-gray-700 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-all font-light"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gold-100">Subject *</label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="bg-gray-900/50 border-gray-700 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-all font-light"
                        placeholder="How can we help?"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gold-100">Message *</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="bg-gray-900/50 border-gray-700 text-white focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-all font-light resize-none"
                        placeholder="Tell us more about your inquiry..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-gold-600 to-gold-400 text-black hover:from-gold-500 hover:to-gold-300 font-bold h-12 text-lg shadow-lg hover:shadow-gold-500/20 transition-all"
                    >
                      {loading ? (
                        <>Sending...</>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" /> Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Social Media & Quick Response */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            {/* Instagram */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 border border-white/10 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-12 bg-pink-500/10 rounded-full blur-2xl group-hover:bg-pink-500/20 transition-colors" />

              <h2 className="text-2xl font-bold text-white mb-4 font-serif flex items-center gap-3">
                <Instagram className="text-pink-500 w-8 h-8" /> Follow Us
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Stay updated with our latest offers, new menu items, and special deals!
                Join our community of food lovers.
              </p>

              <a
                href="https://www.instagram.com/mw_cook_with_magic?igsh=MTU2YjVnNXN4dzRhNA=="
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 rounded-xl bg-gray-900/50 border border-pink-500/30 hover:border-pink-500 hover:bg-pink-900/10 transition-all duration-300 group/link"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 p-[2px]">
                    <div className="w-full h-full bg-black rounded-full flex items-center justify-center">
                      <Instagram className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-gold-100 font-bold text-lg group-hover/link:text-gold-400 transition-colors">@magicworld.pk</h3>
                    <p className="text-gray-500 text-xs">Official Instagram Page</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-500 group-hover/link:text-pink-500 transition-colors" />
                </div>
              </a>
            </div>

            {/* Quick Response - Now Full Width */}
            <div className="bg-gradient-to-br from-gold-900/20 to-gray-900/40 rounded-3xl p-8 border border-gold-500/20 backdrop-blur-sm">
              <div className="flex items-center gap-6">
                <div className="p-4 bg-gold-500/10 rounded-full flex-shrink-0 animate-pulse-slow">
                  <Clock className="w-8 h-8 text-gold-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 font-serif">Lightning Fast Response</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    We value your time. Expect a response within <span className="text-gold-400 font-bold">1-2 hours</span> during business hours.
                    <br />For urgent matters, call us directly.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;

